import { HealthStatus, ProjectStatus } from './useProjectsV2.types';

export const PROJECT_STATUS_CONFIG: Record<
  ProjectStatus,
  {
    label: string;
    color: string;
    bgColor: string;
    clickupColor: string;
  }
> = {
  on_track: {
    label: 'On Track',
    color: 'text-success',
    bgColor: 'bg-success/10',
    clickupColor: '#6bc950',
  },
  at_risk: {
    label: 'At Risk',
    color: 'text-amber-600',
    bgColor: 'bg-amber-500/10',
    clickupColor: '#f9d900',
  },
  off_track: {
    label: 'Off Track',
    color: 'text-red-600',
    bgColor: 'bg-red-500/10',
    clickupColor: '#e50000',
  },
  paused: {
    label: 'Paused',
    color: 'text-slate-600',
    bgColor: 'bg-slate-500/10',
    clickupColor: '#808080',
  },
  completed: {
    label: 'Completed',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/10',
    clickupColor: '#4169e1',
  },
};

export function clickupColorToStatus(hexColor: string | null): ProjectStatus {
  if (!hexColor) return 'on_track';

  const normalized = hexColor.toLowerCase();

  if (
    normalized.includes('6bc950') ||
    normalized.includes('02bcd4') ||
    normalized.includes('4caf50') ||
    normalized.includes('8bc34a')
  ) {
    return 'on_track';
  }
  if (
    normalized.includes('f9d900') ||
    normalized.includes('ffcc00') ||
    normalized.includes('ffc107') ||
    normalized.includes('ff9800')
  ) {
    return 'at_risk';
  }
  if (
    normalized.includes('e50000') ||
    normalized.includes('ff0000') ||
    normalized.includes('f44336') ||
    normalized.includes('e91e63')
  ) {
    return 'off_track';
  }
  if (
    normalized.includes('808080') ||
    normalized.includes('999999') ||
    normalized.includes('9e9e9e') ||
    normalized.includes('607d8b')
  ) {
    return 'paused';
  }
  if (
    normalized.includes('4169e1') ||
    normalized.includes('0000ff') ||
    normalized.includes('2196f3') ||
    normalized.includes('3f51b5')
  ) {
    return 'completed';
  }

  return 'on_track';
}

export function statusToClickupColor(status: ProjectStatus): string {
  return PROJECT_STATUS_CONFIG[status]?.clickupColor || '#6bc950';
}

export function healthToStatus(health: HealthStatus): ProjectStatus {
  switch (health) {
    case 'green':
      return 'on_track';
    case 'yellow':
      return 'at_risk';
    case 'red':
      return 'off_track';
    default:
      return 'on_track';
  }
}

export function getHealthColor(health: HealthStatus): string {
  switch (health) {
    case 'green':
      return 'text-success';
    case 'yellow':
      return 'text-amber-500';
    case 'red':
      return 'text-red-500';
    default:
      return 'text-slate-500';
  }
}

export function getHealthBgColor(health: HealthStatus): string {
  switch (health) {
    case 'green':
      return 'bg-success/10';
    case 'yellow':
      return 'bg-amber-500/10';
    case 'red':
      return 'bg-red-500/10';
    default:
      return 'bg-slate-500/10';
  }
}

export function getHealthLabel(health: HealthStatus): string {
  switch (health) {
    case 'green':
      return 'Healthy';
    case 'yellow':
      return 'At Risk';
    case 'red':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

export function getStatusColor(status: ProjectStatus): string {
  return PROJECT_STATUS_CONFIG[status]?.color || 'text-slate-500';
}

export function getStatusBgColor(status: ProjectStatus): string {
  return PROJECT_STATUS_CONFIG[status]?.bgColor || 'bg-slate-500/10';
}

export function getStatusLabel(status: ProjectStatus): string {
  return PROJECT_STATUS_CONFIG[status]?.label || 'Unknown';
}

export function statusToHealthColor(status: ProjectStatus): HealthStatus {
  switch (status) {
    case 'on_track':
    case 'completed':
      return 'green';
    case 'at_risk':
    case 'paused':
      return 'yellow';
    case 'off_track':
      return 'red';
    default:
      return 'unknown';
  }
}